/**
 * Modal dialog.
 *
 * Two POS-specific behaviours:
 *
 *   Escape closes, but `dismissible={false}` disables that. A payment dialog
 *   mid-transaction should not vanish because a hand brushed the keyboard.
 *
 *   The backdrop click is ignored by default. On a touchscreen, a mis-tap
 *   beside a dialog is common; losing a half-entered payment to one is not
 *   acceptable, so closing has to be deliberate.
 *
 * Below `sm` the dialog is a bottom sheet instead of a centred card: full
 * width, anchored to the bottom edge, rounded only at the top. That is not
 * decoration - a centred card on a phone puts its footer buttons in the middle
 * of the screen and its content under the keyboard, whereas a sheet keeps the
 * confirm button where the thumb already is. The width prop stops meaning
 * anything at that size; every sheet is the width of the screen.
 */

import { useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';

const WIDTHS = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-none sm:max-w-[95vw]'
};

const Modal = ({
    open,
    onClose,
    title,
    subtitle = null,
    children,
    footer = null,
    width = 'md',
    dismissible = true,
    closeOnBackdrop = false,
    className = ''
}) => {
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            if (event.key === 'Escape' && dismissible) {
                event.stopPropagation();
                onClose?.();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, dismissible, onClose]);

    // Move focus into the dialog so keyboard entry - a tendered amount, a
    // discount - goes where the cashier expects without a second tap.
    useEffect(() => {
        if (!open) return;
        const firstInput = panelRef.current?.querySelector(
            'input:not([type="hidden"]), textarea, select, [data-autofocus]'
        );
        (firstInput || panelRef.current)?.focus?.();
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4 no-print"
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div
                className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
                onClick={closeOnBackdrop && dismissible ? onClose : undefined}
            />

            <div
                ref={panelRef}
                tabIndex={-1}
                className={`relative flex max-h-sheet w-full ${WIDTHS[width] || WIDTHS.md}
                            flex-col overflow-hidden rounded-t-2xl bg-white shadow-panel
                            animate-sheet-up pb-safe
                            sm:animate-slide-up sm:rounded-2xl sm:pb-0 ${className}`}
            >
                {(title || dismissible) && (
                    <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:gap-4 sm:px-5 sm:py-4">
                        <div className="min-w-0">
                            {title && (
                                <h2 className="truncate text-base font-semibold text-slate-800 sm:text-lg">{title}</h2>
                            )}
                            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
                        </div>

                        {dismissible && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="-mr-1 -mt-1 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                aria-label="Close"
                            >
                                <FiX className="h-5 w-5" />
                            </button>
                        )}
                    </header>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">{children}</div>

                {/* Stacked on a phone and reversed, so the primary action -
                    which every caller passes last - ends up on top, under the
                    thumb, rather than buried beneath Cancel. */}
                {footer && (
                    <footer className="flex flex-col-reverse items-stretch gap-2 border-t border-slate-200
                                       bg-slate-50 px-4 py-3
                                       sm:flex-row sm:items-center sm:justify-end sm:px-5">
                        {footer}
                    </footer>
                )}
            </div>
        </div>
    );
};

export default Modal;
